import { Component, OnInit } from '@angular/core';
import { Globals } from 'app/_globals';
import { shouldShowWorkerVisibleCompetency, UserCompetency } from 'app/_models/competency.model';
import { COMPETENCY_CATEGORY_BUTTONS, WORKER_SELF_COMPETENCY_STATUS_BUTTONS, matchesCompetencyCategory, matchesCompetencyStatus } from 'app/_utils/competency-filters.utils';
import { buildSummary, FilterSummaryItem } from 'app/_utils/filter-summary.utils';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CompetencyService } from 'app/_services/competency.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteService } from 'app/_services/site.service';

type WorkerCompetencySiteOwnerGroupViewState = 'loading' | 'error' | 'empty' | 'filtered-empty' | 'ready';

interface WorkerCompetencySiteOwnerGroup {
  fullName: string;
  siteOwnerId: number;
  competencies: UserCompetency[];
  visibleCompetencies: UserCompetency[];
  expanded: boolean;
  isLoading: boolean;
  hasError: boolean;
  viewState: WorkerCompetencySiteOwnerGroupViewState;
}

interface WorkerCompetencySiteOwnerLoadResult {
  workerCompetencies: UserCompetency[];
  hasError: boolean;
}

@Component({
  selector: 'app-worker-manage-competencies',
  templateUrl: './worker-manage-competencies.component.html',
})
export class WorkerManageCompetenciesComponent implements OnInit {
  public userId: number;
  public siteOwnerGroups: WorkerCompetencySiteOwnerGroup[] = [];
  public visibleSiteOwnerGroups: WorkerCompetencySiteOwnerGroup[] = [];
  public siteOwnersLoaded = false;
  public hasSiteOwnerLoadError = false;

  public filters = {
    siteOwner: '',
    status: '',
    category: '',
  };

  public statusButtons = WORKER_SELF_COMPETENCY_STATUS_BUTTONS;
  public categoryButtons = COMPETENCY_CATEGORY_BUTTONS;

  constructor(
    private globals: Globals,
    private competencyService: CompetencyService,
    private notificationService: NotificationService,
    private siteService: SiteService,
  ) {
    this.userId = this.globals.user.id;
  }

  ngOnInit() {
    this.getSiteOwnersList();
  }

  //
  // Page Load
  //
  private getSiteOwnersList(): void {
    this.siteService.getAllSitesForWorker({ page: 0, size: 99999 }).subscribe(
      (data: any) => {
        this.hasSiteOwnerLoadError = false;
        const workerSites = Array.isArray(data.body) ? data.body : [];
        const siteOwnersById = {};

        workerSites.forEach(workerSite => {
          const siteOwner = workerSite.site.siteOwner;

          // avoid duplicates in the map
          if (siteOwnersById[siteOwner.id]) {
            return;
          }

          siteOwnersById[siteOwner.id] = {
            fullName: siteOwner.firstName + ' ' + siteOwner.lastName,
            siteOwnerId: siteOwner.id,
          };
        });

        this.siteOwnerGroups = Object.keys(siteOwnersById)
          .map(siteOwnerId => siteOwnersById[siteOwnerId])
          .sort((a, b) => a.fullName.localeCompare(b.fullName))
          .map((siteOwner): WorkerCompetencySiteOwnerGroup => ({
            ...siteOwner,
            competencies: [],
            visibleCompetencies: [],
            expanded: true,
            isLoading: true,
            hasError: false,
            viewState: 'loading',
          }));
        this.visibleSiteOwnerGroups = [...this.siteOwnerGroups];
        this.siteOwnersLoaded = true;

        this.loadCompetenciesForSiteOwners();
      },
      (error) => {
        console.error(error);

        this.siteOwnersLoaded = true;
        this.hasSiteOwnerLoadError = true;
        this.notificationService.notify('danger', 'Error', 'Could not fetch site owner list');
      }
    );
  }

  public loadCompetenciesForSiteOwners(): void {
    this.siteOwnerGroups = this.siteOwnerGroups.map((siteOwnerGroup): WorkerCompetencySiteOwnerGroup => ({
      ...siteOwnerGroup,
      isLoading: true,
      hasError: false,
      viewState: 'loading',
      visibleCompetencies: [],
    }));

    // for each site owner, fetch the user's competencies and site owner approvals
    const requests = this.siteOwnerGroups.map(siteOwnerGroup => {
      const siteOwnerId = siteOwnerGroup.siteOwnerId;

      return this.competencyService.loadUserCompetencyStatus(this.userId, siteOwnerId).pipe(
        map((workerCompetencies) => ({
          workerCompetencies: workerCompetencies.filter((competency) => shouldShowWorkerVisibleCompetency(competency)),
          hasError: false,
        } as WorkerCompetencySiteOwnerLoadResult)),
        // internal catch error returns a error state instead of failing the entire set of requests
        catchError(() => of({
          workerCompetencies: [],
          hasError: true,
        } as WorkerCompetencySiteOwnerLoadResult)),
      );
    });

    forkJoin(requests).subscribe((results: WorkerCompetencySiteOwnerLoadResult[]) => {
      const failedSiteOwners: string[] = [];

      // map each site owner data
      this.siteOwnerGroups = this.siteOwnerGroups.map((siteOwnerGroup, index): WorkerCompetencySiteOwnerGroup => {
        const result = results[index];
        const competencies = result.workerCompetencies;
        const hasCompetencies = competencies.length > 0;

        if (result.hasError) {
          failedSiteOwners.push(siteOwnerGroup.fullName);
        }

        return {
          ...siteOwnerGroup,
          competencies,
          visibleCompetencies: [],
          expanded: hasCompetencies || result.hasError ? true : false,
          isLoading: false,
          hasError: result.hasError,
        };
      });

      // apply filters which will update the visible competencies and viewState
      this.applyAllFilters();

      // alert about any errors
      failedSiteOwners.forEach((siteOwnerFullName) => {
        this.notificationService.notify('danger', 'Error', 'Could not load competencies for ' + siteOwnerFullName);
      });
    });
  }

  private applyAllFilters(): void {
    this.visibleSiteOwnerGroups = this.siteOwnerGroups
      .filter((siteOwnerGroup) => this.matchesSiteOwnerFilter(siteOwnerGroup))
      .map((siteOwnerGroup): WorkerCompetencySiteOwnerGroup => {
      const visibleCompetencies = this.getFilteredCompetencies(siteOwnerGroup);

      return {
        ...siteOwnerGroup,
        visibleCompetencies,
        viewState: this.getSiteOwnerGroupViewState(siteOwnerGroup, visibleCompetencies),
      };
      });
  }

  private matchesSiteOwnerFilter(siteOwnerGroup: WorkerCompetencySiteOwnerGroup): boolean {
    if (!this.filters.siteOwner) {
      return true;
    }

    return siteOwnerGroup.fullName.toLowerCase().includes(this.filters.siteOwner.toLowerCase().trim());
  }

  private getFilteredCompetencies(siteOwnerGroup: WorkerCompetencySiteOwnerGroup): UserCompetency[] {
    return siteOwnerGroup.competencies.filter((competency) =>
      matchesCompetencyStatus(this.filters.status, competency) &&
      matchesCompetencyCategory(this.filters.category, competency)
    );
  }

  private getSiteOwnerGroupViewState(
    siteOwnerGroup: WorkerCompetencySiteOwnerGroup,
    visibleCompetencies: UserCompetency[]
  ): WorkerCompetencySiteOwnerGroupViewState {
    if (siteOwnerGroup.isLoading) {
      return 'loading';
    }

    if (siteOwnerGroup.hasError) {
      return 'error';
    }

    if (!siteOwnerGroup.competencies.length) {
      return 'empty';
    }

    if (!visibleCompetencies.length) {
      return 'filtered-empty';
    }

    return 'ready';
  }

  public get activeFiltersSummary(): FilterSummaryItem[] {
    const summary = [];
    const siteOwnerFilter = this.filters.siteOwner.trim();
    const statusSummary = buildSummary('Status', this.filters.status, this.statusButtons);
    const categorySummary = buildSummary('Category', this.filters.category, this.categoryButtons);

    if (siteOwnerFilter) {
      summary.push({
        label: 'Site Owner',
        value: siteOwnerFilter,
      });
    }

    if (statusSummary) {
      summary.push(statusSummary);
    }

    if (categorySummary) {
      summary.push(categorySummary);
    }

    return summary;
  }

  //
  // Page interaction
  //
  public setStatusFilter(key: string) {
    this.filters.status = key;
    this.applyAllFilters();
  }

  public setSiteOwnerFilter(filterText: string): void {
    this.filters.siteOwner = filterText;
    this.applyAllFilters();
  }

  public setCategoryFilter(key: string) {
    this.filters.category = key;
    this.applyAllFilters();
  }

  public onCardUpdated(): void {
    this.loadCompetenciesForSiteOwners();
  }

  public toggleSiteOwnerGroup(siteOwnerId: number): void {
    this.siteOwnerGroups = this.siteOwnerGroups.map((siteOwnerGroup) => (
      siteOwnerGroup.siteOwnerId === siteOwnerId
        ? { ...siteOwnerGroup, expanded: !siteOwnerGroup.expanded }
        : siteOwnerGroup
    ));
    this.applyAllFilters();
  }
}
